// src/components/DiveStories.js
// Bandeau "stories" en haut de la carte : plongées à venir des ami·es / clubs
// (et publiques) avec la photo de l'organisateur. Au tap -> fiche + Rejoindre.
import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Clock, Users, MapPin, X } from "lucide-react";
import { fetchWithAuth } from "../utils/api";
import "./DiveStories.css";

const apiUrl = process.env.REACT_APP_API_URL;

function initials(first, last) {
  const a = (first || "").trim()[0] || "";
  const b = (last || "").trim()[0] || "";
  return (a + b).toUpperCase() || "?";
}

function Avatar({ organizer, highlighted, size }) {
  const [imgOk, setImgOk] = useState(true);
  const url = organizer?.profile_image_url;
  const cls = `ds-avatar${highlighted ? " ds-avatar-hl" : ""}`;
  const style = size ? { width: size, height: size } : undefined;
  return (
    <span className={cls} style={style}>
      {url && imgOk ? (
        <img src={url} alt="" onError={() => setImgOk(false)} />
      ) : (
        <span className="ds-initials">{initials(organizer?.first_name, organizer?.last_name)}</span>
      )}
    </span>
  );
}

export default function DiveStories({ currentUser }) {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [joining, setJoining] = useState(false);

  const load = useCallback(() => {
    if (!currentUser?.id) return;
    setLoading(true);
    fetchWithAuth(`${apiUrl}/feed/upcoming-dives`)
      .then((r) => r.json())
      .then((data) => setItems(Array.isArray(data) ? data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [currentUser?.id]);

  useEffect(() => { load(); }, [load]);

  const formatDay = (date, time) => {
    const d = new Date(`${date}T${(time || "00:00").slice(0, 5)}:00`);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const dd = new Date(d); dd.setHours(0, 0, 0, 0);
    const diff = Math.round((dd - today) / 86400000);
    if (diff === 0) return t("stories.today");
    if (diff === 1) return t("stories.tomorrow");
    return d.toLocaleDateString(i18n.language, { weekday: "short", day: "numeric" });
  };
  const formatTime = (time) => (time || "").slice(0, 5);

  const join = async (dive) => {
    setJoining(true);
    try {
      const res = await fetchWithAuth(`${apiUrl}/join-dive`, {
        method: "POST",
        body: JSON.stringify({ dive_intention_id: dive.id }),
      });
      if (!res.ok) throw new Error();
      toast.success(t("map.toasts.diveJoined"));
      setSelected((s) => (s ? { ...s, joined: true, participantCount: s.participantCount + 1 } : s));
      load();
      window.dispatchEvent(new Event("badges-check"));
    } catch {
      toast.error(t("map.toasts.joinError"));
    } finally {
      setJoining(false);
    }
  };

  if (!currentUser?.id) return null;
  // Rien tant que ça charge, ou s'il n'y a aucune plongée à venir :
  // on n'affiche rien du tout, la carte prend toute la hauteur.
  if (loading || items.length === 0) return null;

  return (
    <div className="ds-wrap">
      <div className="ds-row">
        {items.map((d) => {
              const hl = d.isFriend || d.isClub || d.joined || d.isOrganizer;
              return (
                <button key={d.id} type="button" className="ds-item" onClick={() => setSelected(d)}>
                  <Avatar organizer={d.organizer} highlighted={hl} />
                  <span className="ds-label">{formatDay(d.planned_date, d.planned_time)}</span>
                  <span className="ds-sub">{d.site?.name || ""}</span>
                </button>
              );
            })}
      </div>

      {selected && (
        <div className="ds-sheet-backdrop" onClick={() => setSelected(null)}>
          <div className="ds-sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <button type="button" className="ds-close" onClick={() => setSelected(null)} aria-label="Fermer">
              <X size={18} />
            </button>

            <div className="ds-sheet-head">
              <Avatar organizer={selected.organizer} highlighted size={54} />
              <div className="ds-sheet-head-txt">
                <strong>{selected.site?.name || "—"}</strong>
                <span>
                  {(selected.organizer?.first_name || "").trim()} {t("stories.organizes")}
                </span>
              </div>
            </div>

            <div className="ds-sheet-body">
              <span className="ds-info">
                <Clock size={15} /> {formatDay(selected.planned_date, selected.planned_time)} · {formatTime(selected.planned_time)}
              </span>
              <span className="ds-info">
                <Users size={15} />{" "}
                {t(selected.participantCount <= 1 ? "stories.participants_one" : "stories.participants_other", {
                  count: selected.participantCount,
                })}
              </span>
              {selected.site?.canton && (
                <span className="ds-info">
                  <MapPin size={15} /> {selected.site.canton}
                </span>
              )}
            </div>

            <div className="ds-sheet-actions">
              {selected.isOrganizer ? (
                <span className="ds-badge-state">{t("stories.yourDive")}</span>
              ) : selected.joined ? (
                <span className="ds-badge-state ds-badge-joined">{t("stories.joined")}</span>
              ) : (
                <button type="button" className="ds-join" disabled={joining} onClick={() => join(selected)}>
                  {joining ? "…" : t("stories.join")}
                </button>
              )}
              <button
                type="button"
                className="ds-map"
                onClick={() => {
                  const sid = selected.site?.id;
                  setSelected(null);
                  if (sid) {
                    window.dispatchEvent(
                      new CustomEvent("sda:focus-site", { detail: { siteId: sid } })
                    );
                  }
                }}
              >
                {t("stories.viewOnMap")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
